if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        // Ensure this modal only closes via the explicit close button.
        // ModalDialog's default backdrop click-to-close is disabled here.
        this.addEventListener(
          'click',
          (event) => {
            if (event.target === this) event.stopImmediatePropagation();
          },
          true
        );

        this.modalGallery = this.querySelector('[data-modal-gallery]');
        if (this.modalGallery) {
          // Prevent any modal-level "click outside media" close behavior.
          this.modalGallery.addEventListener('pointerup', (event) => event.stopPropagation());
        }
        this.initModalGallery();
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      initModalGallery() {
        if (this.modalGalleryInitialized) return;
        this.modalGalleryInitialized = true;

        this.modalViewer = this.querySelector('[data-modal-viewer]');
        this.modalThumbnails = this.querySelector('[data-modal-thumbnails]');
        this.modalMql = window.matchMedia('(min-width: 1025px)');

        if (this.modalViewer) {
          this.modalViewer.addEventListener('slideChanged', debounce(this.onModalSlideChanged.bind(this), 150));
        }

        if (this.modalThumbnails) {
          this.modalThumbnails.addEventListener('click', (event) => {
            const button = event.target.closest('button[data-modal-thumb][data-media-id]');
            if (!button) return;
            event.preventDefault();
            this.scrollModalViewerToMedia(button.dataset.mediaId);
          });
        }

        this.setupModalVerticalThumbnailsHeightSync();
      }

      setupModalVerticalThumbnailsHeightSync() {
        if (!this.modalViewer || !this.modalThumbnails) return;

        const shouldSync = () =>
          this.modalThumbnails.dataset.orientation === 'vertical' && this.modalMql && this.modalMql.matches;

        const sync = () => {
          if (!shouldSync()) {
            this.modalThumbnails.style.removeProperty('height');
            return;
          }
          const viewerHeight = Math.round(this.modalViewer.getBoundingClientRect().height);
          if (viewerHeight > 0) this.modalThumbnails.style.height = `${viewerHeight}px`;
        };

        sync();
        this.modalViewerResizeObserver = new ResizeObserver(sync);
        this.modalViewerResizeObserver.observe(this.modalViewer);
        if (this.modalMql) this.modalMql.addEventListener('change', sync);
      }

      onModalSlideChanged(event) {
        const mediaId = event?.detail?.currentElement?.dataset?.mediaId;
        if (!mediaId) return;
        this.setActiveModalThumbnail(mediaId);
      }

      scrollModalViewerToMedia(mediaId) {
        if (!mediaId) return;
        const slide = this.querySelector(`[data-modal-slide][data-media-id="${mediaId}"]`);
        if (!slide) return;

        const viewer = this.modalViewer || slide.closest('[data-modal-viewer]');
        if (viewer && typeof viewer.resetPages === 'function') viewer.resetPages();

        requestAnimationFrame(() => {
          if (viewer && viewer.slider) {
            viewer.slider.scrollTo({ left: slide.offsetLeft, top: slide.offsetTop, behavior: 'smooth' });
          } else {
            slide.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
          }
          this.setActiveModalThumbnail(mediaId);
        });
      }

      setActiveModalThumbnail(mediaId) {
        if (!this.modalThumbnails || !mediaId) return;
        const buttons = Array.from(this.modalThumbnails.querySelectorAll('button[data-modal-thumb][data-media-id]'));
        if (!buttons.length) return;

        buttons.forEach((b) => b.removeAttribute('aria-current'));
        const activeButton = buttons.find((b) => b.dataset.mediaId === String(mediaId)) || buttons[0];
        if (!activeButton) return;
        activeButton.setAttribute('aria-current', 'true');

        const activeItem = activeButton.closest('.slider__slide') || activeButton.closest('li');
        if (!activeItem) return;

        if (typeof this.modalThumbnails.isSlideVisible === 'function' && this.modalThumbnails.isSlideVisible(activeItem, 10))
          return;

        if (this.modalThumbnails.slider) {
          const verticalThumbs =
            this.modalThumbnails.dataset.orientation === 'vertical' && this.modalMql && this.modalMql.matches;
          this.modalThumbnails.slider.scrollTo(
            verticalThumbs ? { top: activeItem.offsetTop, behavior: 'smooth' } : { left: activeItem.offsetLeft, behavior: 'smooth' }
          );
        }
      }

      showActiveMedia() {
        const targetMediaId = this.openedBy && this.openedBy.getAttribute('data-media-id');
        if (!targetMediaId) return;

        const modalSlides = this.querySelectorAll('[data-modal-slide][data-media-id]');
        const hasSlides = modalSlides && modalSlides.length > 0;

        if (hasSlides) {
          modalSlides.forEach((element) => {
            element.classList.remove('active');
          });

          let activeSlide = this.querySelector(`[data-modal-slide][data-media-id="${targetMediaId}"]`);
          if (!activeSlide) activeSlide = modalSlides[0];
          if (!activeSlide) return;

          activeSlide.classList.add('active');

          const sliderComponent = activeSlide.closest('slider-component');
          if (sliderComponent && typeof sliderComponent.resetPages === 'function') sliderComponent.resetPages();

          requestAnimationFrame(() => {
            if (sliderComponent && sliderComponent.slider) {
              sliderComponent.slider.scrollTo({ left: activeSlide.offsetLeft, top: activeSlide.offsetTop, behavior: 'auto' });
            } else {
              activeSlide.scrollIntoView({ block: 'nearest', inline: 'center' });
            }
          });

          const activeDeferredMedia = activeSlide.querySelector('deferred-media');
          if (activeDeferredMedia) {
            const activeMediaTemplate = activeDeferredMedia.querySelector('template');
            const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
            if (activeMediaContent && activeMediaContent.querySelector('.js-youtube')) activeDeferredMedia.loadContent();
          }

          this.setActiveModalThumbnail(activeSlide.dataset.mediaId || targetMediaId);
          return;
        }

        // Fallback for legacy modal markup (media elements as direct children)
        this.querySelectorAll(`[data-media-id]:not([data-media-id="${targetMediaId}"])`).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${targetMediaId}"]`);
        if (!activeMedia) return;

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        if (container) container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (activeMedia.nodeName == 'DEFERRED-MEDIA' && activeMediaContent && activeMediaContent.querySelector('.js-youtube'))
          activeMedia.loadContent();
      }
    }
  );
}
